import cx from 'classnames';
import assetUrl from 'mk/common/assetUrl';
import Btn, { BtnCountType, BtnLayout } from 'mk2/components/Btn';
import Emojis from 'mk2/components/forms/Emojis';
import PhotosFieldNew from 'mk2/components/forms/PhotosFieldNew';
import Img from 'mk2/components/Img';
import ImgCropped, { ImgCropMode } from 'mk2/components/ImgCropped';
import Portal from 'mk2/components/PortalLoadable';
import { FormPageType } from 'mk2/constants';
import { default as PhotosUpload, PhotosUploadConfig, PhotoUploadType } from 'mk2/containers/PhotosUpload/PhotosUpload';
import {
    toolbarCloseAll,
    toolbarInsertEmoji,
    toolbarOpenEmojis,
    toolbarOpenPhotos,
    toolbarOpenStickers,
    toolbarPreparePhotosUploadTrigger,
    toolbarWatchEmojiStart,
    toolbarWatchEmojiStop,
} from 'mk2/containers/Toolbar/Toolbar.actions';
import { ToolbarAction } from 'mk2/containers/Toolbar/Toolbar.enums';
import styles from 'mk2/containers/Toolbar/Toolbar.mscss';
import { mkReduxFormOpenPage } from 'mk2/helpers/form.actions';
import {
    FailedPhotoUpload,
    PendingPhotoUpload,
    PhotoUploadStatus,
    SuccessfulPhotoUpload,
} from 'mk2/helpers/form.reducers';
import Loadable from 'mk2/helpers/Loadable';
import { scrollDocumentTo } from 'mk2/helpers/scrolling';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { AppState, getRequestDeviceIsIOS, getRequestDeviceMobile, getToolbarState } from 'mk2/reducers';
import { PhotoEntity, PhotoSchema } from 'mk2/schemas';
import { getDenormalizedEntity } from 'mk2/selectors';
import React from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';

const StickerField = Loadable({
    loader: () => import('mk2/components/forms/StickerField' /* webpackChunkName: "components.forms.StickerField" */),
    modules: ['mk2/components/forms/StickerField'],
    webpack: () => [ require.resolveWeak('mk2/components/forms/StickerField') ],
});

interface OwnProps {
    className?: string;
    // meno formu, pre ktory je toolbar pouzity
    formName: string;

    // button zobrazeny na pravo v riadku s ikonami fotaku a stickera
    rightAction?: React.ReactNode;
    // input zobrazeny nad riadkom s ikonami fotaku a stickera
    textArea?: React.ReactNode;

    // Additional buttons send from parent
    additionalActions?: React.ReactNode;

    // emojis button len na desktope. na mobile nie je vobec (tam je nativna klavesnica)
    emojis?: {
        fields: string[]; // List of input fields where we can insert emoji
        defaultField: string; // Default field where we insert emoji after click when no field was focused
    };

    // button na upload fotiek. Ak null, toolbar button na upload fotiek nema
    photos?: PhotosUploadConfig; // TODO: rename var to photosUploadConfig
    stickers?: boolean;

    isInBlogs?: boolean;
    isInForum?: boolean;
    isInGroups?: boolean;
    isInWiki?: boolean;
    isInAdminclub?: boolean;

    isFixed?: boolean;
    isGray?: boolean;
    isBlack?: boolean;
    passiveMode?: boolean; // if true, upload next photo button is hidden and no file selection popup will display

    // counselling reply - unauthenticated upload (e.g. accessKey='counselling_reply:slug:pE96mf3AUfB4mLJv')
    accessKey?: string;

    handleOpenFileExplorerFunctionProcessor?(handleOpenPhotosFn: any);
    onPrepareUploadPhotosCustom?(formName: string, config: PhotosUploadConfig, files: File[], processPhotoUploadOnFormPage: FormPageType, accessKey?: string);
}

interface StateProps {
    opened: ToolbarAction;
    isIOS: boolean;
    isMobile: boolean;

    currentlySelectedSticker: number;

    currentPhotos: Array<PendingPhotoUpload | SuccessfulPhotoUpload | FailedPhotoUpload>;
    currentPhotosFirstPhoto?: PhotoEntity;  // photo from first upload, if we have it
}

interface DispatchProps {
    onClose(formName: string);
    onOpenEmojisInPlace(formName: string);
    onOpenPhotos(formName: string);
    onPrepareUploadPhotos(formName: string, config: PhotosUploadConfig, files: File[], processPhotoUploadOnFormPage: FormPageType, accessKey?: string);
    onOpenStickersInPlace(formName: string);

    onInsertEmoji(formName: string, shortcode: string);
    onWatchEmojiStart(formName: string, fields: string[], defaultField: string);
    onWatchEmojiStop(formName: string);

    onOpenFormPage(formName, formPage);
}

type ToolbarProps = OwnProps & StateProps & DispatchProps;

class Toolbar extends React.PureComponent<ToolbarProps> {

    public static defaultProps: Partial<OwnProps> = {
        stickers: true,
        isInWiki: false,
        isInAdminclub: false,
    };

    private photosFieldRef: PhotosFieldNew;

    public componentDidMount() {
        const { emojis, onWatchEmojiStart, formName, handleOpenFileExplorerFunctionProcessor } = this.props;
        const hasEmojis = !!emojis;

        if (hasEmojis) {
            onWatchEmojiStart(formName, emojis.fields, emojis.defaultField);
        }

        if (handleOpenFileExplorerFunctionProcessor) {
            handleOpenFileExplorerFunctionProcessor(this.handleOpenFileExplorer);
        }
    }

    public componentWillUnmount() {
        const { formName, onClose, onWatchEmojiStop } = this.props;

        // Close opened in the state
        onClose(formName);

        onWatchEmojiStop(formName);
    }

    public componentWillReceiveProps(nextProps: ToolbarProps) {
        const currentHasEmojis = !!this.props.emojis;
        const nextHasEmojis = !!nextProps.emojis;

        if (currentHasEmojis && !nextHasEmojis) {
            this.props.onWatchEmojiStop(this.props.formName);
        } else if (!currentHasEmojis && nextHasEmojis) {
            nextProps.onWatchEmojiStart(nextProps.formName, nextProps.emojis.fields, nextProps.emojis.defaultField);
        }
    }

    public render() {
        const {
            formName, className, emojis, photos, stickers, opened, textArea,
            isFixed, isGray, isBlack, isIOS, isMobile, rightAction, isInBlogs, isInForum, isInGroups, isInAdminclub,
            currentlySelectedSticker, currentPhotos, currentPhotosFirstPhoto, passiveMode,
            additionalActions,
        } = this.props;

        const hasPhotos = !!photos;
        const hasStickers = stickers;
        const hasEmojis = !!emojis && (!isMobile || isInBlogs);  // emojis nie su na mobile (tam ich ma klavesnica)

        const fixed = (isFixed !== undefined) ? isFixed : !isInBlogs && isMobile && !isIOS;
        const isMobileOrIOS =  isMobile || isIOS;
        const numConfirmedSuccessfulUploads = currentPhotos ? currentPhotos.filter((u) => (u.status === PhotoUploadStatus.SUCCESS)).length : 0;

        const showEmojisModal = hasEmojis && opened === ToolbarAction.EMOJIS;
        const showPhotosModal = hasPhotos && opened === ToolbarAction.PHOTOS && (!isMobileOrIOS || isInBlogs || isInGroups);
        const showStickersModal = hasStickers && opened === ToolbarAction.STICKERS;
        const showPhotoCode = hasPhotos && photos.showPhotoCode;
        const disableRotate = hasPhotos && photos.disableRotate;
        const previewPhotoSize = hasPhotos ? photos.previewPhotoSize : null;
        const miniPreviewPhotoSize = hasPhotos && photos.previewPhotoSize || 's150x150';

        const handleOpenFileExplorer = photos && photos.multiple ? this.handleOpenFileExplorer : null;

        return (
            <div
                className={cx(styles.Toolbar, className, {
                    [styles['Toolbar--inBlogs']]: isInBlogs,
                    [styles['Toolbar--inForum']]: isInForum,
                    [styles['Toolbar--inAdminclub']]: isInAdminclub,
                    [styles['Toolbar--inGroups']]: isInGroups,
                    [styles['Toolbar--opened']]: opened !== ToolbarAction.NONE,
                    [styles['Toolbar--fixed']]: fixed,
                    [styles['Toolbar--blueTheme']]: !isGray && !isBlack && ((isMobileOrIOS && isInForum) || isInGroups),
                    [styles['Toolbar--grayTheme']]: isGray,
                    [styles['Toolbar--blackTheme']]: isBlack,
                })}
            >
                {!isInBlogs && textArea && (
                    <div
                        className={cx({
                            [styles['Toolbar__textAreaWrapper--inForum']]: isInForum && !isMobileOrIOS,
                        })}
                    >
                        {textArea}
                    </div>
                )}
                <div className={styles.Toolbar__actions}>
                    <div className={styles.Toolbar__actions__left}>
                    {hasEmojis && !isInBlogs && (
                        <Btn
                            className={styles.Toolbar__actions__action}
                            layout={BtnLayout.Icon}
                            icon="fa-smile-o"
                            onClick={this.handleOpenEmojis}
                        />
                    )}
                    {hasPhotos && !currentlySelectedSticker && [
                        currentPhotosFirstPhoto ? (
                            <Btn
                                key="btn-img"
                                className={styles.Toolbar__actions_action}
                                layout={BtnLayout.Icon}
                                img={
                                    <ImgCropped
                                        photo={currentPhotosFirstPhoto}
                                        photoVersion={miniPreviewPhotoSize}
                                        height={20}
                                        width={20}
                                        mode={ImgCropMode.Cover}
                                    />
                                }
                                count={numConfirmedSuccessfulUploads}
                                countType={BtnCountType.Circle}
                                onClick={this.handleOpenPhotos}
                            />
                        ) : (
                            <Btn
                                key="btn-icon"
                                className={cx(
                                    styles.Toolbar__actions__action,
                                    styles.Toolbar__actions__action__photos,
                                    (!hasStickers || currentPhotos.length > 0) && styles.Toolbar__actions__action__no_stickers,
                                )}
                                layout={BtnLayout.Icon}
                                icon="fa-camera"
                                onClick={this.handleOpenPhotos}
                            />
                        ),
                        // this has to be always rendered, so that <input type="file"> is in dom
                        // and we can call it's click() to show file open dialog
                        <PhotosFieldNew
                            key="fileinput"
                            name="photos"
                            config={photos}
                            ref={this.handlePhotosFileInputFieldRef}
                            onUploadFiles={this.handleUploadFiles}
                        />,
                    ]}
                    {hasStickers && (currentPhotos.length === 0) && (
                        ((isMobileOrIOS && isInForum) || isInGroups) && !currentlySelectedSticker ? (
                            <Btn
                                className={cx(
                                    styles.Toolbar__actions__action,
                                    styles.Toolbar__actions__action__stickers,
                                )}
                                layout={BtnLayout.Icon}
                                img={
                                    <Img
                                        src={assetUrl + 'img/icons/sticker_blue_placeholder.png'}
                                        width={25}
                                        height={25}
                                    />
                                }
                                onClick={this.handleOpenStickers}
                            />
                        ) : (
                            <Btn
                                className={cx(
                                    styles.Toolbar__actions__action,
                                    styles.Toolbar__actions__action__stickers,
                                    (!hasPhotos || currentlySelectedSticker) && styles.Toolbar__actions__action__no_photos,
                                    (isMobileOrIOS && currentlySelectedSticker) && styles.Toolbar__actions__selectedSticker,
                                )}
                                layout={BtnLayout.Icon}
                                icon={'sticker-' + (currentlySelectedSticker || 0)}
                                onClick={this.handleOpenStickers}
                            />
                        )
                    )}
                    {hasEmojis && isInBlogs && (
                        <Btn
                            className={cx(styles.Toolbar__actions__action, styles.Toolbar__actions__action__emojis)}
                            layout={BtnLayout.Icon}
                            icon="fa-smile-o"
                            onClick={this.handleOpenEmojis}
                        />
                    )}
                    {additionalActions && (
                        <div className={styles.Toolbar__actions__additionalActionsWrapper}>
                            {additionalActions}
                        </div>
                    )}
                    </div>
                    {rightAction && (!isInBlogs || (isInBlogs && !isMobile))  && (
                        <div className={styles.Toolbar__actions__right}>
                            {rightAction}
                        </div>
                    )}
                </div>

                {(showEmojisModal || showPhotosModal || showStickersModal) && (
                    <div className={styles.Toolbar__modal}>
                        {/* Ignore height for desktop - show everything */}
                        {showEmojisModal && (
                            <Emojis
                                onSelect={this.handleEmojiInsert}
                            />
                        )}
                        {showPhotosModal && (
                            <PhotosUpload
                                formName={formName}
                                photoUploadType={PhotoUploadType.REGULAR_PHOTOS}
                                onClickUploadNextPhoto={passiveMode ? null : handleOpenFileExplorer}
                                previewPhotoSize={previewPhotoSize}
                                showPhotoCode={showPhotoCode}
                                disableRotate={disableRotate}
                            />
                        )}
                        {showStickersModal && (
                            <StickerField
                                name="sticker"
                                onSelect={this.handleStickerSelected}
                            />
                        )}
                    </div>
                )}

                {rightAction && isInBlogs && isMobile && (
                    <div className={styles.Toolbar__actions__right}>
                        {rightAction}
                    </div>
                )}
            </div>
        );
    }

    private handleEmojiInsert = (shortcode: string) => {
        const { formName, onInsertEmoji } = this.props;

        onInsertEmoji(formName, shortcode);
    };

    private handleStickerSelected = (stickerId) => {
        const { formName, onClose } = this.props;

        // always close toolbar (also when current sticker is unselected)
        onClose(formName);
    };

    private handlePhotosFileInputFieldRef = (ref: PhotosFieldNew) => {
        this.photosFieldRef = ref;
    };

    private handleOpenFileExplorer = () => {
        // no photos yet, show file open dialog
        this.photosFieldRef.openFileExplorer();
    };

    private handleUploadFiles = (config: PhotosUploadConfig, files: File[]) => {
        const { formName, onPrepareUploadPhotos, isMobile, isIOS, isInBlogs, isInGroups, accessKey, onPrepareUploadPhotosCustom } = this.props;
        const processPhotoUploadOnFormPage = (!(isMobile || isIOS) || isInBlogs || isInGroups) ? FormPageType.Main : FormPageType.Photos;
        if (onPrepareUploadPhotosCustom) {
            onPrepareUploadPhotosCustom(formName, config, files, processPhotoUploadOnFormPage, accessKey);
        } else {
            onPrepareUploadPhotos(formName, config, files, processPhotoUploadOnFormPage, accessKey);
        }
        this.handleOpenPhotos(false, true);
    };

    private handleOpenPhotos = (openFileDialog: boolean = true, justOpenNoToggle: boolean = false) => {
        const {
            isMobile, isIOS, isInBlogs, isInGroups, isInWiki,
            onOpenFormPage, onOpenPhotos,
            opened, formName, currentPhotos, passiveMode,
        } = this.props;

        const isMobileOrIOS =  isMobile || isIOS;

        if (openFileDialog && (!currentPhotos || currentPhotos.length === 0)) {
            if (!passiveMode) {
                this.handleOpenFileExplorer();
            }
        } else {
            if (isMobileOrIOS && !isInBlogs && !isInGroups) {
                onOpenFormPage(formName, FormPageType.Photos);
                scrollDocumentTo('top');
            } else {
                if (!justOpenNoToggle || (justOpenNoToggle && opened !== ToolbarAction.PHOTOS)) {
                    onOpenPhotos(formName);
                    if (!isInBlogs && !isInGroups && !isInWiki) {
                        scrollDocumentTo('bottom');
                    }
                }
            }
        }
    };

    private handleOpenEmojis = () => {
        const { formName, isInBlogs, isInGroups, isMobile, isIOS, onOpenEmojisInPlace } = this.props;

        const isMobileOrIOS =  isMobile || isIOS;

        if (isMobileOrIOS && !isInBlogs && !isInGroups) {
            // pass, on mobile there are no emojis in the Toolbar except blogs
        } else {
            onOpenEmojisInPlace(formName);
            if (!isInBlogs && !isInGroups) {
                scrollDocumentTo('bottom');
            }
        }
    };

    private handleOpenStickers = () => {
        const { formName, onOpenFormPage, isInBlogs, isInGroups, isMobile, isIOS, onOpenStickersInPlace } = this.props;

        const isMobileOrIOS =  isMobile || isIOS;

        if (isMobileOrIOS && !isInBlogs && !isInGroups) {
            // open stickers in new form page
            onOpenFormPage(formName, FormPageType.Stickers);
            scrollDocumentTo('top');
        } else {
            // open stickers at the bottom of the form
            onOpenStickersInPlace(formName);
            if (!isInBlogs && !isInGroups) {
                scrollDocumentTo('bottom');
            }
        }
    };
}

const container = () => document.body;

class ToolbarWrapper extends React.Component<ToolbarProps> {
    public render() {
        const { isMobile, isIOS, isInBlogs, isInGroups } = this.props;
        return ((!isMobile || isIOS || isInBlogs || isInGroups) ? (
            <Toolbar {...this.props} />
        ) : (
            <Portal container={container}>
                <Toolbar {...this.props} />
            </Portal>
        ));
    }
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    const { formName } = ownProps;

    const selector = formValueSelector(formName);
    const toolbarState = getToolbarState(state, formName);

    const currentPhotos = selector(state, 'photos') as any[] || [];
    const currentPhotosFirstPhoto: PhotoEntity =
        (currentPhotos && currentPhotos.length > 0 && currentPhotos[0].status === PhotoUploadStatus.SUCCESS)
            ? getDenormalizedEntity<PhotoEntity>(state, PhotoSchema, (currentPhotos[0] as SuccessfulPhotoUpload).photoId)
            : null;

    return {
        opened: toolbarState.opened,
        isIOS: getRequestDeviceIsIOS(state),
        isMobile: getRequestDeviceMobile(state),
        currentlySelectedSticker: selector(state, 'sticker') as number,
        currentPhotos,
        currentPhotosFirstPhoto,
    };
}

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {
    onClose: toolbarCloseAll,
    onOpenEmojisInPlace: toolbarOpenEmojis,
    onOpenPhotos: toolbarOpenPhotos,
    onPrepareUploadPhotos: toolbarPreparePhotosUploadTrigger,
    onOpenStickersInPlace: toolbarOpenStickers,
    onOpenFormPage: mkReduxFormOpenPage,

    onInsertEmoji: toolbarInsertEmoji,
    onWatchEmojiStart: toolbarWatchEmojiStart,
    onWatchEmojiStop: toolbarWatchEmojiStop,
};

export const ComposeToolbar = connect(mapStateToProps, mapDispatchToProps)(ToolbarWrapper);
